import { useMemo } from "react";
import { formatMoney, torontoToday, NETSUITE_ACCOUNT_IDS } from "@utils";
import { useJournalEntryLineLookups } from "@common";

const SFC_BANK_ID = NETSUITE_ACCOUNT_IDS.RBC_SF_CORPORATION_9411; // 1009
const LOAN_ACCOUNT_ID = NETSUITE_ACCOUNT_IDS.LOAN_FROM_SMILES_FIRST_CORPORATION; // 4940

function cleanBankName(name) {
  return (name || "").replace(/^(?:\s*Bank Balance\s*:\s*)+/i, "").trim();
}

// Entity (subsidiary) name for an account option — the clinic name, or "SFC"
// for the corporate account. NOT the bank name.
function entityName(opt) {
  if (!opt) return "";
  if (Number(opt.bank_account_id) === Number(SFC_BANK_ID)) return "SFC";
  return opt.clinic_name || opt.clinic || "";
}

function fmt(v) {
  return v == null ? "" : formatMoney(v);
}

/**
 * Preview of the intercompany journal entry that mirrors a transfer, laid out
 * like the Create Intercompany Journal Entry flow. The 4 balanced lines are
 * derived from the from/to accounts; account **full names** are resolved from
 * NetSuite by id (same lookup the JE preview uses).
 */
export default function IntercompanyTransferPreview({ fromOpt, toOpt, amount, transferDate }) {
  const amt = Number(amount);
  const ready = !!fromOpt && !!toOpt && amt > 0;
  const fromName = entityName(fromOpt);
  const toName = entityName(toOpt);

  // Fixed defaults (read-only): transaction date follows the transfer's Date
  // selection; memo is the standard wording.
  const date = transferDate || torontoToday();
  const memo = `Intercompany ${fromName} to ${toName} - Smiles Analytics`;

  // Line definitions: bank leg + intercompany loan leg per side.
  const lineDefs = ready
    ? [
        {
          accountId: fromOpt.bank_account_id,
          fallback: cleanBankName(fromOpt.bank_name),
          subsidiary: fromName,
          credit: amt,
        },
        {
          accountId: LOAN_ACCOUNT_ID,
          fallback: "Loan: From Smiles First Corporation",
          subsidiary: fromName,
          debit: amt,
        },
        {
          accountId: toOpt.bank_account_id,
          fallback: cleanBankName(toOpt.bank_name),
          subsidiary: toName,
          debit: amt,
        },
        {
          accountId: LOAN_ACCOUNT_ID,
          fallback: "Loan: From Smiles First Corporation",
          subsidiary: toName,
          credit: amt,
        },
      ]
    : [];

  // Payload shape expected by the shared JE lookup hook (resolves account names).
  const payload = useMemo(
    () => ({ line: { items: lineDefs.map((l) => ({ account: { id: l.accountId } })) } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, fromOpt?.bank_account_id, toOpt?.bank_account_id],
  );
  const { accountNameById, accountsQuery } = useJournalEntryLineLookups(payload);

  if (!ready) return null;

  const acctName = (l) =>
    accountNameById[String(l.accountId)] || (accountsQuery.isLoading ? "Loading…" : l.fallback);

  return (
    <div className="mt-6 border-t border-palette-gray-divider pt-5">
      <h4 className="mb-3 text-sm font-bold text-palette-gray-primary">Intercompany transfer</h4>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-palette-gray-secondary">
            Header subsidiary
          </div>
          <div className="text-sm font-medium text-palette-gray-primary">{fromName}</div>
        </div>
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-palette-gray-secondary">
            Transaction date
          </div>
          <div className="text-sm font-medium text-palette-gray-primary">{date}</div>
        </div>
      </div>

      <div className="mt-3">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-palette-gray-secondary">
          Memo
        </div>
        <div className="text-sm text-palette-gray-primary">{memo}</div>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-palette-gray-divider">
        <table className="cf-table w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle text-xs uppercase tracking-wide text-palette-gray-secondary">
              <th className="px-3 py-2 text-left font-semibold">Account</th>
              <th className="px-3 py-2 text-left font-semibold">Subsidiary</th>
              <th className="px-3 py-2 text-right font-semibold">Debit</th>
              <th className="px-3 py-2 text-right font-semibold">Credit</th>
            </tr>
          </thead>
          <tbody>
            {lineDefs.map((l, i) => (
              <tr
                key={i}
                className={`border-b border-palette-gray-divider last:border-0 ${
                  l.debit != null ? "bg-palette-emerald-soft" : "bg-palette-rose-soft"
                }`}
              >
                <td className="px-3 py-2 text-palette-gray-primary">{acctName(l)}</td>
                <td className="px-3 py-2 text-palette-gray-secondary">{l.subsidiary}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-palette-gray-primary">
                  {fmt(l.debit)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-palette-gray-primary">
                  {fmt(l.credit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
